import { Component, inject, Input, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute } from '@angular/router';
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';
import { GetDictionaryDTO } from 'src/app/Common/common.interface';
import { UserMasterService } from 'src/app/Admin/user-master/user-master.service';
import { CustomerSubscriptionDTO } from 'src/app/AdvERP/companies/company-customer-subscriptions/company-customer-subscriptions.interface';
import { UserLicensesDTO, UserLicensesSummaryDTO } from './company-user-licenses.interface';
import { UserLicensesService } from './company-user-licenses.service';
import { UserMasterGetDictionaryRequestDTO } from 'src/app/Admin/user-master/user-master.interface';

@Component({
  selector: 'app-company-user-licenses',
  templateUrl: './company-user-licenses.component.html',
  styleUrls: ['./company-user-licenses.component.css']
})
export class CompanyUserLicensesComponent implements OnChanges, OnDestroy {

  @Input({ required: true }) tenantId!: number;
  @Input() subscription: CustomerSubscriptionDTO | null = null;

  User = inject(UserLoginService).User();
  modalService = inject(NgbModal);
  route = inject(ActivatedRoute);
  zone = inject(NgZone);
  userLicensesService = inject(UserLicensesService);
  userMasterService = inject(UserMasterService);

  private donutRoot?: am5.Root;
  private barRoot?: am5.Root;

  isDataLoading: boolean = false;
  licenseItems: UserLicensesDTO[] = [];
  summaryData: UserLicensesSummaryDTO | null = null;
  userList: GetDictionaryDTO[] = [];

  /* Assign form */
  assignModalRef!: NgbModalRef;
  editingLicenseId: number | null = null;   // null = new assign, set = editing existing slot
  assignForm = new FormGroup({
    userId: new FormControl<number | null>(null),
    licenseType: new FormControl<string>('INCLUDED'),
    validFrom: new FormControl<string>(''),
    validTo: new FormControl<string>('')
  });
  assignErrorMsg: string = '';

  get availableIncluded(): number {
    if (this.summaryData) return Math.max(0, this.summaryData.includedPlanLimit - this.summaryData.includedOccupied);
    return Math.max(0, this.maxIncluded - this.occupiedIncluded);
  }

  get availablePremium(): number {
    if (this.summaryData) return Math.max(0, this.summaryData.premiumPlanLimit - this.summaryData.premiumOccupied);
    return this.licenseItems.filter(l => l.licenseType === 'PREMIUM' && !l.userId).length;
  }

  get licenseTypeOptions() {
    return [
      { value: 'INCLUDED', label: 'Included', available: this.availableIncluded,  limited: true  },
      { value: 'PREMIUM',  label: 'Premium',  available: this.availablePremium,   limited: true  },
      { value: 'FREE',     label: 'Free',     available: null,                    limited: false }
    ];
  }

  get occupiedIncluded(): number {
    if (this.summaryData) return this.summaryData.includedOccupied;
    return this.licenseItems.filter(l => l.licenseType === 'INCLUDED' && l.userId).length;
  }

  get occupiedPremium(): number {
    if (this.summaryData) return this.summaryData.premiumOccupied;
    return this.licenseItems.filter(l => l.licenseType === 'PREMIUM' && l.userId).length;
  }

  get maxPremium(): number {
    if (this.summaryData) return this.summaryData.premiumPlanLimit;
    return this.licenseItems.filter(l => l.licenseType === 'PREMIUM').length;
  }

  get maxIncluded(): number {
    // IncludedPlanLimit = SP.IncludedUsers (base seats from plan, not CS.ActiveUsers)
    if (this.summaryData) return this.summaryData.includedPlanLimit;
    if (this.subscription?.activeUsers) return this.subscription.activeUsers;
    return this.licenseItems.filter(l => l.licenseType === 'INCLUDED').length;
  }

  get totalOccupied(): number {
    return this.licenseItems.filter(l => l.userId).length;
  }

  get availableCount(): number {
    // Plan capacity minus occupied — NOT empty slot rows
    if (this.summaryData) return this.totalPlanLimit - this.summaryData.totalOccupied;
    return this.availableIncluded + this.availablePremium;
  }

  get occupiedCount(): number {
    if (this.summaryData) return this.summaryData.totalOccupied;
    return this.totalOccupied;
  }

  /** Total plan capacity = IncludedUsers + (ExtraUserBlocks × ExtraUserBlockSize).
   *  Falls back to client-side sum in case backend hasn't been redeployed yet. */
  get totalPlanLimit(): number {
    if (!this.summaryData) return this.licenseItems.length;
    return this.summaryData.totalPlanLimit
      || (this.summaryData.includedPlanLimit + this.summaryData.premiumPlanLimit);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tenantId'] && changes['tenantId'].currentValue) {
      this.loadLicenses();
      this.loadSummary();
      this.loadUsers();
    }
  }

  loadUsers(): void {
    if (!this.tenantId) return;
    const data : UserMasterGetDictionaryRequestDTO = {
      tenantId: this.tenantId,
      actionUser: '0'
    }
    this.userMasterService.UserMaster_GetDictionary(data)
      .subscribe({
        next: (res) => {
          this.userList = res?.dropDownList ?? [];
        }
      });
  }

  loadLicenses(): void {
    if (!this.tenantId) return;
    this.isDataLoading = true;
    this.userLicensesService.UserLicenses_readByTenantId({ tenantId: this.tenantId })
      .subscribe({
        next: (res) => {
          this.licenseItems = res?.items ?? [];
          this.isDataLoading = false;
        },
        error: () => {
          this.isDataLoading = false;
        }
      });
  }

  loadSummary(): void {
    if (!this.tenantId) return;
    this.userLicensesService.UserLicenses_summary({ tenantId: this.tenantId })
      .subscribe({
        next: (res: UserLicensesSummaryDTO) => {
          this.summaryData = res;
          setTimeout(() => this.renderCharts(), 50);
        }
      });
  }

  openAssignModal(template: any): void {
    this.editingLicenseId = null;
    // Default dates from active subscription
    const defaultFrom = this.subscription?.startDate
      ? this.subscription.startDate.substring(0, 10)
      : new Date().toISOString().substring(0, 10);
    const defaultTo = this.subscription?.endDate
      ? this.subscription.endDate.substring(0, 10)
      : '';

    const defaultType = this.availableIncluded > 0 ? 'INCLUDED'
      : this.availablePremium > 0 ? 'PREMIUM'
      : 'FREE';

    this.assignForm.reset({
      userId: null,
      licenseType: defaultType,
      validFrom: defaultFrom,
      validTo: defaultTo
    });
    this.assignErrorMsg = '';
    this.assignModalRef = this.modalService.open(template, { backdrop: 'static', size: 'md' });
  }

  openEditModal(template: any, license: UserLicensesDTO): void {
    this.editingLicenseId = license.userLicenseId;
    const defaultFrom = this.subscription?.startDate
      ? this.subscription.startDate.substring(0, 10)
      : new Date().toISOString().substring(0, 10);
    const defaultTo = this.subscription?.endDate
      ? this.subscription.endDate.substring(0, 10)
      : '';
    this.assignForm.reset({
      userId: license.userId ?? null,
      licenseType: license.licenseType ?? 'INCLUDED',
      validFrom: license.validFrom ? license.validFrom.substring(0, 10) : defaultFrom,
      validTo: license.validTo ? license.validTo.substring(0, 10) : defaultTo
    });
    this.assignErrorMsg = '';
    this.assignModalRef = this.modalService.open(template, { backdrop: 'static', size: 'md' });
  }

  confirmAssign(): void {
    const userId = this.assignForm.value.userId;
    const licenseType = this.assignForm.value.licenseType;
    const validFrom = this.assignForm.value.validFrom;
    const validTo = this.assignForm.value.validTo;
    if (!userId) {
      this.assignErrorMsg = 'Please select a user.';
      return;
    }
    if (!licenseType) {
      this.assignErrorMsg = 'Please select a license type.';
      return;
    }
    this.userLicensesService.UserLicenses_assign({
      userLicenseId: this.editingLicenseId ?? undefined,
      tenantId: this.tenantId,
      userId: userId,
      licenseType: licenseType,
      validFrom: validFrom || undefined,
      validTo: validTo || undefined,
      actionUser: this.User.userId.toString()
    }).subscribe({
      next: () => {
        this.assignModalRef?.close();
        this.loadLicenses();
      },
      error: (err) => {
        this.assignErrorMsg = err?.error?.message ?? 'Failed to assign license.';
      }
    });
  }

  /** Deterministic colour for user avatars */
  getUserColor(name: string): string {
    const palette = ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981','#3b82f6','#14b8a6','#f97316'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return palette[Math.abs(hash) % palette.length];
  }

  deleteSlot(license: UserLicensesDTO): void {
    const userName = license.userName ? `"${license.userName}"` : 'this empty slot';
    if (!confirm(`Delete ${userName}?\nThis will permanently remove the license slot and cannot be undone.`)) return;

    this.userLicensesService.UserLicenses_delete({
      userLicenseId: license.userLicenseId,
      tenantId: this.tenantId,
      actionUser: this.User.userId.toString()
    }).subscribe({
      next: () => this.loadLicenses()
    });
  }

  renderCharts(): void {
    this.zone.runOutsideAngular(() => {
      // ── Donut: Available vs Occupied ──────────────────────────────
      if (this.donutRoot) this.donutRoot.dispose();
      const donutEl = document.getElementById('ul-donut-chart');
      if (donutEl) {
        this.donutRoot = am5.Root.new('ul-donut-chart');
        this.donutRoot.setThemes([am5themes_Animated.new(this.donutRoot)]);

        const chart = this.donutRoot.container.children.push(
          am5percent.PieChart.new(this.donutRoot, {
            innerRadius: am5.percent(60),
            layout: this.donutRoot.horizontalLayout
          })
        );

        const series = chart.series.push(
          am5percent.PieSeries.new(this.donutRoot, {
            valueField: 'value',
            categoryField: 'category',
            alignLabels: false
          })
        );
        series.labels.template.setAll({ text: '', fill: am5.color(0xffffff) });
        series.ticks.template.set('visible', false);
        series.slices.template.setAll({ cornerRadius: 4, strokeWidth: 2, stroke: am5.color(0xffffff) });

        // Use plan capacity (limit − occupied), NOT empty slot rows
        const occupied = this.summaryData ? this.summaryData.totalOccupied : this.occupiedCount;
        const planCapacity = this.totalPlanLimit;
        const available = Math.max(0, planCapacity - occupied);
        series.data.setAll([
          { category: 'Occupied', value: occupied,  fill: am5.color(0x6366f1) },
          { category: 'Available', value: available, fill: am5.color(0x22c55e) }
        ]);
        series.slices.template.adapters.add('fill', (fill, target) => {
          const dp = target.dataItem?.dataContext as any;
          return dp?.fill ?? fill;
        });
        series.slices.template.adapters.add('stroke', (stroke, target) => {
          const dp = target.dataItem?.dataContext as any;
          return dp?.fill ?? stroke;
        });

        // Centre label
        this.donutRoot.container.children.push(am5.Label.new(this.donutRoot, {
          text: `[fontSize:22px fontWeight:700]${occupied}[/]\n[fontSize:11px]Occupied[/]`,
          textAlign: 'center',
          centerX: am5.percent(50),
          centerY: am5.percent(50),
          x: am5.percent(33),
          y: am5.percent(50)
        }));

        const legend = chart.children.push(am5.Legend.new(this.donutRoot, {
          centerY: am5.percent(50),
          y: am5.percent(50),
          layout: this.donutRoot.verticalLayout,
          marginLeft: 20
        }));
        legend.data.setAll(series.dataItems);
        series.appear(1000, 100);
      }

      // ── Bar: breakdown by type (occupied vs available) ─────────────
      if (this.barRoot) this.barRoot.dispose();
      const barEl = document.getElementById('ul-bar-chart');
      if (barEl) {
        this.barRoot = am5.Root.new('ul-bar-chart');
        this.barRoot.setThemes([am5themes_Animated.new(this.barRoot)]);

        const chart = this.barRoot.container.children.push(
          am5xy.XYChart.new(this.barRoot, {
            panX: false, panY: false,
            layout: this.barRoot.verticalLayout
          })
        );

        const yRenderer = am5xy.AxisRendererY.new(this.barRoot, { minGridDistance: 20 });
        const yAxis = chart.yAxes.push(am5xy.CategoryAxis.new(this.barRoot, {
          categoryField: 'type',
          renderer: yRenderer
        }));
        const xAxis = chart.xAxes.push(am5xy.ValueAxis.new(this.barRoot, {
          min: 0,
          renderer: am5xy.AxisRendererX.new(this.barRoot, { strokeOpacity: 0.1 })
        }));

        const makeBar = (name: string, field: string, color: number) => {
          const series = chart.series.push(am5xy.ColumnSeries.new(this.barRoot!, {
            name, xAxis, yAxis,
            valueXField: field,
            categoryYField: 'type',
            stacked: true,
            sequencedInterpolation: true,
            fill: am5.color(color),
            stroke: am5.color(color)
          }));
          series.columns.template.setAll({
            tooltipText: '{name}: {valueX}',
            height: am5.percent(60),
            cornerRadiusTR: 3, cornerRadiusBR: 3
          });
          return series;
        };

        // Available = plan limit − occupied (capacity-based, not empty slot rows)
        const s = this.summaryData;
        const barData = [
          {
            type: 'INCLUDED',
            occupied: s ? s.includedOccupied : this.occupiedIncluded,
            available: s ? Math.max(0, s.includedPlanLimit - s.includedOccupied) : this.availableIncluded
          },
          {
            type: 'PREMIUM',
            occupied: s ? s.premiumOccupied : this.occupiedPremium,
            available: s ? Math.max(0, s.premiumPlanLimit - s.premiumOccupied) : this.availablePremium
          },
          {
            type: 'FREE',
            occupied: s ? s.freeOccupied : 0,
            available: 0   // unlimited — don't show capacity bar for free
          }
        ];
        const data = barData.filter(d => d.occupied + d.available > 0);

        const oSeries = makeBar('Occupied', 'occupied', 0x6366f1);
        const aSeries = makeBar('Available', 'available', 0x22c55e);

        yAxis.data.setAll(data);
        oSeries.data.setAll(data);
        aSeries.data.setAll(data);

        const legend = chart.children.push(am5.Legend.new(this.barRoot, {
          centerX: am5.p50, x: am5.p50
        }));
        legend.data.setAll(chart.series.values);
        chart.appear(1000, 100);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.donutRoot) this.donutRoot.dispose();
    if (this.barRoot) this.barRoot.dispose();
  }
}
